import { createPlugin } from '@customerio/dryad';
import type { ValidationIssue } from '@customerio/dryad';
import { ConditionType } from './condition-types';
import {
  createOptOutBitmapFilter,
  decodeBitmapName,
  optOutChannelForBitmapType,
  unwrapOptOutBitmapNode,
} from './named-bitmap';
import type { NamedBitmapFilter } from './wire-types';
import { DEFAULT_OPT_OUT_CHANNEL, type OptOutChannel } from './opt-out-channel';

// The proto opt-out leaf: `{ named_bitmap: { type: 22 | 33, name: <base64
// phone number> } }`, negation as a `{ not: ... }` wrapper. The channel is
// carried entirely by the bitmap type number (22 = SMS, 33 = WhatsApp) — there
// is no channel field on the wire — and the name is the base64 of the sender
// phone number, with the empty string meaning "any number".
//
// The `named_bitmap` leaf key is shared with the message condition (type 3);
// the two plugins gate on disjoint bitmap type numbers.

export type OptOutConditionState = {
  channel: OptOutChannel;
  // '' is a legitimate value (the picker's "Any number"); null means nothing
  // has been picked yet, which blocks the commit.
  phoneNumber: string | null;
  negate: boolean;
};

/**
 * A null number only occurs while the number select is untouched; '' is
 * accepted as "Any number".
 */
export function validateOptOutCondition(state: OptOutConditionState): Required<ValidationIssue>[] {
  if (state.phoneNumber == null) {
    return [{ message: "Phone number can't be blank.", field: 'phoneNumber' }];
  }
  return [];
}

export type OptOutNegateWording = {
  optedOut: string;
  notOptedOut: string;
};

export const DEFAULT_OPT_OUT_NEGATE_WORDING: OptOutNegateWording = {
  optedOut: 'has opted out of',
  notOptedOut: 'has not opted out of',
};

function decodeBase64PhoneNumber(name: string): string | null {
  try {
    return decodeBitmapName(name);
  } catch {
    return null;
  }
}

function decodeOptOutNode(json: unknown): OptOutConditionState | null {
  const unwrapped = unwrapOptOutBitmapNode(json);
  if (unwrapped == null) {
    return null;
  }
  const phoneNumber = decodeBase64PhoneNumber(unwrapped.leaf.named_bitmap.name);
  if (phoneNumber == null) {
    return null;
  }
  return {
    channel: optOutChannelForBitmapType(unwrapped.leaf.named_bitmap.type),
    phoneNumber,
    negate: unwrapped.negated,
  };
}

/**
 * `createOptOutBitmapFilter` re-derives the bitmap type from the channel and
 * re-encodes the number. A null phoneNumber can't reach the wire (validation
 * blocks the commit), so the fallback only keeps the encoder total.
 */
function serializeOptOutFilter(state: OptOutConditionState): NamedBitmapFilter | { not: NamedBitmapFilter } {
  const filter = createOptOutBitmapFilter(state.phoneNumber ?? '', state.channel);
  if (state.negate) {
    return { not: filter };
  }
  return filter;
}

/**
 * The default channel is workspace-dependent (the first channel with a
 * configured sender), which a pure plugin can't know — the host component
 * that mounts the builder supplies it via the builder's `seeds` option, which
 * replaces the default state fresh leaves are created with per condition
 * type. Parsed leaves are never seeded.
 */
export const peopleFilterOptOutPlugin = createPlugin({
  type: ConditionType.OptOut,
  label: 'Phone number',
  parse: decodeOptOutNode,
  serialize: serializeOptOutFilter,
  validate: validateOptOutCondition,
  createDefault: (): OptOutConditionState => ({
    channel: DEFAULT_OPT_OUT_CHANNEL,
    phoneNumber: null,
    negate: false,
  }),
});
